import React, { useState } from "react";

import styles from "./OrderForm.module.scss";

import { GiCancel } from "react-icons/gi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getFormStep,
  getParties,
  updateFormStep,
  updateOrdersData,
} from "../../features/newOrder/newOrderSlice";
import {
  Controller,
  SubmitHandler,
  useForm,
  useFieldArray,
} from "react-hook-form";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/order";
import { POST } from "../../services/axios.service";
import { route } from "../../static/route";
import { useNavigate } from "react-router-dom";

const OrderForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const parties = useAppSelector(getParties);
  const formStep = useAppSelector(getFormStep);

    const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
    const [itemOption, setItemOption] = useState<OptionsProps[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [requestError, setRequestError] = useState<string | null>(null);

    const {
      control,
      handleSubmit,
      trigger,
      register,
      setValue,
      clearErrors,
      getValues,
      reset,
      watch,
      formState: { errors },
    } = useForm<IFormInput>();

    const { fields, append, remove } = useFieldArray({
      control,
      name: "items",
    });

    // Watch items array for reactive updates
    const watchedItems = watch("items");

    // Initialize party options on mount
    React.useEffect(() => {
      const options: OptionsProps[] = [];
      parties?.forEach((party) => {
        options.push({ value: party.id, label: party.firm_name });
      });
      setPartiesOption(options);
    }, [parties]);

    const handleNextStep = async () => {
      if (formStep === 1) {
        const isPartyValid = await trigger("party_id");
        if (isPartyValid) {
          dispatch(updateFormStep(formStep + 1));
        }
      }
    };

    const handleChangeParty = (party_id: number) => {
      if (party_id) {
        const party = parties.find((p) => p.id === party_id);
        if (party) {
          const options: OptionsProps[] = [];
          party.items?.forEach((item) => {
            if (options.find((o) => o.value === item.item_id) === undefined) {
              options.push({
                value: item.item_id,
                label: item.item.name,
                image: item.item.image,
                weight: item.item.weight,
              });
            }
          });
          setItemOption(options);
        }

        setValue("party_id", party_id);
        // Start with one empty item row
        setValue("items", [{ item_id: 0, qty: null }]);
        clearErrors("party_id");
      }
    };

    const handleChangeItem = (item_id: number, index: number) => {
      if (item_id) {
        // Check if item already added in another row
        const existingItems = getValues("items") || [];
        const isDuplicate = existingItems.some((i, idx) => i.item_id === item_id && idx !== index);
        if (!isDuplicate) {
          setValue(`items.${index}.item_id`, item_id);
          clearErrors(`items.${index}.item_id`);
        }
      }
    };

    const handleAddNewItem = () => {
      append({ item_id: 0, qty: null });
    };

    const getItemName = (item_id: number) => {
      return itemOption.find((o) => o.value === item_id)?.label || "";
    };

    const getItemImage = (item_id: number) => {
      return itemOption.find((o) => o.value === item_id)?.image || null;
    };

    const getItemWeight = (item_id: number) => {
      return itemOption.find((o) => o.value === item_id)?.weight || 0;
    };

    // Check if all current items have valid selection (item_id > 0 and qty > 0)
    const canAddNewItem = () => {
      if (!watchedItems || watchedItems.length === 0) return true;
      return watchedItems.every((item) => item.item_id > 0 && item.qty && item.qty > 0);
    };

    const onSubmit: SubmitHandler<IFormInput> = async (data) => {
      setIsLoading(true);
      setRequestError(null);

      try {
        // Filter out items with no selection (item_id = 0)
        const validItems = data.items.filter((item) => item.item_id > 0 && item.qty && item.qty > 0);

        if (validItems.length > 0) {
          const requestData = {
            party_id: data.party_id,
            items: validItems,
          };

          await POST("/v1/worker/orders", requestData)
            .then(async (response: any) => {
              if (response.data.status === "success") {
                await dispatch(updateFormStep(1));
                await dispatch(updateOrdersData(response.data.data || []));
                await reset();
                navigate(route.newOrderList);
              } else {
                setRequestError(response.data.message);
              }
            })
            .catch((error: any) => {
              setRequestError(error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
            });
        } else {
          setRequestError("નવા કામમાં ૧ આઈટમ ઉમેરવી જરૂરી છે.");
        }
      } catch (error) {
        setRequestError("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
      }

      setIsLoading(false);
    };

    return (
      <div className={styles.OrderForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {formStep === 1 && (
            <div className="w-full flex flex-col justify-center">
              <div className="mt-6">
                <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                  <Controller
                    {...register("party_id", { required: true })}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={partiesOption.find(
                          (val) => val.value === field?.value
                        )}
                        className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                        styles={selectStyles}
                        placeholder={"પાર્ટી નું નામ દાખલ કરો"}
                        options={partiesOption}
                        onChange={(val: any) => handleChangeParty(val?.value)}
                      />
                    )}
                  />
                </div>
                {errors.party_id && errors.party_id.type === "required" && (
                  <span className="text-red-500">પાર્ટી નું નામ સિલેક્ટ કરો</span>
                )}
              </div>

              <button
                className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                type="button"
                onClick={handleNextStep}
              >
                આગળ વધો
              </button>
            </div>
          )}

          {formStep === 2 && (
            <div className="w-full flex flex-col justify-center">
              {/* Item rows */}
              {fields.map((field, index) => {
                const currentItem = watchedItems?.[index];
                const itemId = currentItem?.item_id || 0;
                const qty = currentItem?.qty || 0;
                const weight = getItemWeight(itemId);
                const totalWeight = weight * qty;

                return (
                  <div
                    key={field.id}
                    className="relative bg-white border-2 border-primary-1 rounded p-3 mb-4"
                  >
                    <h2 className="relative w-full text-xl underline text-primary-1 font-bold mb-3">
                      આઈટમ - {index + 1}
                      {fields.length > 1 && (
                        <GiCancel
                          onClick={() => remove(index)}
                          className="absolute cursor-pointer z-10 shadow bg-white top-0 right-0 text-2xl text-primary-1 border-2 border-primary-1 p-1 rounded-full"
                        />
                      )}
                    </h2>

                    {/* Item Select */}
                    <div className="mb-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-14 w-full relative rounded">
                        <Controller
                          name={`items.${index}.item_id`}
                          control={control}
                          rules={{ required: true, min: 1 }}
                          render={({ field: selectField }) => (
                            <Select
                              value={
                                itemOption.find(
                                  (val) => val.value === selectField.value
                                ) || null
                              }
                              className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                              styles={selectStyles}
                              placeholder={"આઈટમ સિલેક્ટ કરો"}
                              options={itemOption}
                              onChange={(val: any) =>
                                handleChangeItem(val?.value, index)
                              }
                            />
                          )}
                        />
                      </div>
                      {errors?.items?.[index]?.item_id && (
                        <span className="text-red-500 text-sm">
                          આઈટમ સિલેક્ટ કરો
                        </span>
                      )}
                    </div>

                    {/* Show weight if item is selected */}
                    {itemId > 0 && (
                      <div className="mb-3 flex items-center gap-2">
                        <span className="text-primary-1 font-semibold">વજન:</span>
                        <span className="text-primary-2 font-bold text-lg">
                          {weight} કિલો
                        </span>
                      </div>
                    )}

                    {/* Quantity Input */}
                    <div className="mb-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="number"
                          step="1"
                          min="1"
                          pattern="[0-9]*"
                          inputMode="numeric"
                          placeholder="નંગ ની સંખ્યા દાખલ કરો"
                          onKeyDown={(e) => {
                            if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') {
                              e.preventDefault();
                            }
                          }}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                          {...register(`items.${index}.qty`, {
                            required: true,
                            min: 1,
                            valueAsNumber: true,
                            validate: (value) => Number.isInteger(value) || "પૂર્ણાંક સંખ્યા દાખલ કરો"
                          })}
                        />
                      </div>
                      {errors?.items?.[index]?.qty && (
                        <span className="text-red-500 text-sm">
                          નંગ ની સંખ્યા દાખલ કરો
                        </span>
                      )}
                    </div>

                    {/* Show total weight if item and qty are valid */}
                    {itemId > 0 && qty > 0 && (
                      <div className="flex items-center gap-2 p-2 rounded">
                        <span className="text-primary-1 font-semibold">કુલ વજન:</span>
                        <span className="text-primary-2 font-bold text-lg">
                          {totalWeight.toFixed(3)} કિલો
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add Item Button */}
              <div className="text-center mb-4">
                <button
                  className="mx-auto shadow rounded-3xl text-2xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-8 py-2 disabled:opacity-50"
                  type="button"
                  onClick={handleAddNewItem}
                  disabled={!canAddNewItem()}
                >
                  + આઈટમ ઉમેરો
                </button>
              </div>

              {requestError !== null && (
                <span className="text-red-500 text-center mt-2 mb-2">
                  {requestError}
                </span>
              )}

              <button
                className="mx-auto mt-4 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2 disabled:opacity-50"
                type="submit"
                disabled={isLoading || fields.length === 0}
              >
                {isLoading ? "રાહ જુઓ..." : "પુરુ કરો"}
              </button>
            </div>
          )}
        </form>
      </div>
    );
  };
  export default OrderForm;